import { isMobileApp } from './screenUtils';

/**
 * The timer's desktop notice (timer-alerts spec, Petr 2026-10-31): one per session, each new moment replacing the last
 * (one tag). As few words as can be read in the moment it shows, and no buttons: on a Mac they hide in a hover menu
 * and the notice slides away before anyone finds them. A click brings Shovel forward and pulses the top bar's pill.
 * Shown through public/timer-notice-sw.js; where that can't run, a plain notice.
 */

const WORKER_URL = '/timer-notice-sw.js';
const WORKER_SCOPE = '/timer-notice/';
const MESSAGE = 'shovel-timer-notice';

export type NoticePermission = NotificationPermission | 'unsupported';

const tagOf = (plannedTaskId: number) => `shovel-timer-${plannedTaskId}`;

export const noticeSupported = () => typeof window !== 'undefined' && 'Notification' in window && !isMobileApp;

export const noticePermission = (): NoticePermission => (noticeSupported() ? Notification.permission : 'unsupported');

let registration: Promise<ServiceWorkerRegistration | null> | null = null;

const noticeWorker = () => {
  if (!registration) {
    registration =
      'serviceWorker' in navigator
        ? navigator.serviceWorker.register(WORKER_URL, { scope: WORKER_SCOPE }).catch(() => null)
        : Promise.resolve(null);
  }
  return registration;
};

/** Gets the notice's worker ready ahead of the first notice, once the browser allows notices. */
export const prepareNotices = () => {
  if (noticePermission() === 'granted') noticeWorker();
};

/** Asks the browser, from a click. Resolves the answer. */
export const askNoticePermission = async (): Promise<NoticePermission> => {
  if (!noticeSupported()) return 'unsupported';
  try {
    const answer = await Notification.requestPermission();
    if (answer === 'granted') noticeWorker();
    return answer;
  } catch {
    return Notification.permission;
  }
};

const fallbacks = new Map<number, Notification>();

export type TimerNotice = {
  plannedTaskId: number;
  checkIn: number;
  title: string;
  body: string;
  /** Quiet when the chime plays, or late: one sound, never a startle. */
  silent: boolean;
};

export const showTimerNotice = async (notice: TimerNotice, onFallbackClick: () => void) => {
  if (noticePermission() !== 'granted') return false;
  const options: NotificationOptions & { renotify?: boolean } = {
    body: notice.body,
    tag: tagOf(notice.plannedTaskId),
    renotify: true,
    // On screen until dealt with, where the browser allows it: it closes itself once answered in Shovel, paused or
    // logged (Petr, 2026-10-31: it slid away too soon).
    requireInteraction: true,
    silent: notice.silent,
    icon: '/icons-192x192.png',
    data: { kind: 'shovel-timer', plannedTaskId: notice.plannedTaskId, checkIn: notice.checkIn }
  };
  const worker = await noticeWorker();
  if (worker) {
    try {
      await worker.showNotification(notice.title, options);
      return true;
    } catch {
      // Falls back to a plain notice below.
    }
  }
  try {
    fallbacks.get(notice.plannedTaskId)?.close();
    const plain = new Notification(notice.title, options);
    plain.onclick = () => {
      window.focus();
      plain.close();
      onFallbackClick();
    };
    fallbacks.set(notice.plannedTaskId, plain);
    return true;
  } catch {
    return false;
  }
};

/** Answered, logged, paused or ended: its notice goes. */
export const closeTimerNotices = async (plannedTaskId: number) => {
  fallbacks.get(plannedTaskId)?.close();
  fallbacks.delete(plannedTaskId);
  if (noticePermission() !== 'granted' || !registration) return;
  const worker = await registration;
  const open = await worker?.getNotifications({ tag: tagOf(plannedTaskId) }).catch(() => []);
  open?.forEach(notice => notice.close());
};

export type NoticeClick = { plannedTaskId: number; checkIn: number };

/** A click on a notice, as the worker hands it to this tab. Returns the unsubscribe. */
export const onNoticeClick = (listener: (click: NoticeClick) => void) => {
  if (!('serviceWorker' in navigator)) return () => undefined;
  const handle = (event: MessageEvent) => {
    const data = event.data;
    if (data?.type !== MESSAGE) return;
    listener({ plannedTaskId: Number(data.plannedTaskId), checkIn: Number(data.checkIn) });
  };
  navigator.serviceWorker.addEventListener('message', handle);
  return () => navigator.serviceWorker.removeEventListener('message', handle);
};

// The ask in the timer row: its "Not now" hides it for the next 5 timers started (timer-alerts spec).
const STARTS_KEY = 'shovel.timerStarts';
const ASK_HIDDEN_KEY = 'shovel.noticeAskHiddenUntil';
const HIDE_FOR_STARTS = 5;

const readNumber = (key: string) => {
  try {
    return Number(window.localStorage.getItem(key)) || 0;
  } catch {
    return 0;
  }
};

const writeNumber = (key: string, value: number) => {
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    // Private windows: the ask comes back sooner.
  }
};

export const noteTimerStarted = () => writeNumber(STARTS_KEY, readNumber(STARTS_KEY) + 1);

export const noticeAskHidden = () => readNumber(ASK_HIDDEN_KEY) > readNumber(STARTS_KEY);

export const hideNoticeAsk = () => writeNumber(ASK_HIDDEN_KEY, readNumber(STARTS_KEY) + HIDE_FOR_STARTS);
